import { createFileRoute, Navigate, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { useAppAuth } from "@timeghost/auth/react/app-initializer";
import { SidebarInset, SidebarProvider } from "@timeghost/ui/shadcn/sidebar";
import { Spinner } from "@timeghost/ui/shadcn/spinner";
import { type CSSProperties, type JSX, lazy, type ReactNode, Suspense, useEffect, useState } from "react";
import OnboardingChrome, { OnboardingDraftProvider } from "@/components/onboarding/onboarding-chrome";
import UnsavedLeaveDialog from "@/components/overlays/unsaved-leave-dialog";
import VersionUpdateDialog from "@/components/overlays/version-update-dialog";
import SpinnerPage from "@/components/pages/spinner-page";
import AppHeader from "@/components/shell/app-header";
import AppSidebar from "@/components/shell/app-sidebar";
import WorkspaceBillingGate, { useWorkspaceUsageBlocked } from "@/components/shell/workspace-billing-gate";
import WorkspaceChangeDialog from "@/components/shell/workspace-change-dialog";
import { useCreateTimeEntryDialogStore } from "@/components/timer/use-create-time-entry-dialog";
import { useCreateWorkingHoursDialogStore } from "@/components/timer/use-create-workinghours-dialog";
import RouteGuard from "@/lib/auth/route-guard";
import { NavPermissionsProvider, useNavPermissions } from "@/lib/hooks/use-nav-permissions";
import { useProfileLanguage } from "@/lib/i18n/use-profile-language";
import { canAccessNavPath, firstAllowedNavPath } from "@/lib/permissions/nav-route-access";
import { hasUserRoleManager, isAdmin, resolveNavPermissions, toPermissionUser } from "@/lib/permissions/permission";
import { useTimesRealtime } from "@/lib/signalr/use-times-realtime";
import stores from "@/lib/stores";
import { setStoredSidebarOpen, useStoredSidebarOpen } from "@/lib/stores/sidebar-store";
import { emailFromRawUser } from "@/lib/testing/can-access-testing";
import { useWorkingHoursSuggestionStore } from "@/lib/workinghours/mixed-times-suggestion-store";
import { isOnboardingPath, workspaceNeedsOnboarding } from "@/lib/workspace/onboarding";
import { currentUserLacksLicense, licenseGateAllowsPath } from "@/lib/workspace/subscription-status";
import { useCurrentUser } from "@/queries/user/useCurrentUser.query";
import { useUserTheme } from "@/queries/user/useUserTheme";

const CreateTimeEntryDialogHost = lazy(() => import("@/components/timer/create-time-entry-dialog-host"));
const CreateWorkingHoursDialogHost = lazy(() => import("@/components/timer/create-workinghours-dialog-host"));
const TimerWidgetHost = lazy(() => import("@/components/timer/timer-widget-host"));
const WorkingHoursSuggestionDialogHost = lazy(() => import("@/components/timer/workinghours-suggestion-dialog-host"));

function LazyCreateTimeEntryDialogHost(): JSX.Element | null {
	const open = useCreateTimeEntryDialogStore((state) => state.open);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		if (open) {
			setMounted(true);
		}
	}, [open]);

	if (!mounted) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<CreateTimeEntryDialogHost />
		</Suspense>
	);
}

function LazyCreateWorkingHoursDialogHost(): JSX.Element | null {
	const open = useCreateWorkingHoursDialogStore((state) => state.open);
	const absenceOpen = useCreateWorkingHoursDialogStore((state) => state.absenceOpen);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		if (open || absenceOpen) {
			setMounted(true);
		}
	}, [absenceOpen, open]);

	if (!mounted) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<CreateWorkingHoursDialogHost />
		</Suspense>
	);
}

function LazyTimerWidgetHost(): JSX.Element {
	return (
		<Suspense fallback={null}>
			<TimerWidgetHost />
		</Suspense>
	);
}

function LazyWorkingHoursSuggestionDialogHost(): JSX.Element | null {
	const open = useWorkingHoursSuggestionStore((state) => state.open);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		if (open) {
			setMounted(true);
		}
	}, [open]);

	// No tracking-mode gate: callers await the dialog result, so an unmounted host would hang the save.
	if (!mounted) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<WorkingHoursSuggestionDialogHost />
		</Suspense>
	);
}

function AppSessionOverlays(): JSX.Element {
	return (
		<>
			<WorkspaceChangeDialog />
			<UnsavedLeaveDialog />
			<VersionUpdateDialog />
		</>
	);
}

function NavRouteGate({ children }: { readonly children: ReactNode }): JSX.Element {
	const pathname = useRouterState({ select: (state) => state.location.pathname });
	const permissions = useNavPermissions();
	if (permissions.isLoading || !permissions.hasUser) {
		return (
			<div className="flex min-h-0 flex-1 items-center justify-center p-8">
				<Spinner className="text-muted-foreground" size="sm" />
			</div>
		);
	}
	if (!canAccessNavPath(pathname, permissions)) {
		return <Navigate replace to={firstAllowedNavPath(permissions)} />;
	}
	return <>{children}</>;
}

const AppLayout = (): JSX.Element => {
	const { authMode } = useAppAuth();
	const { isInitialLoading, rawUser, user } = useCurrentUser();
	const isSidenavOpen = useStoredSidebarOpen();
	const usageBlocked = useWorkspaceUsageBlocked();
	const pathname = useRouterState({ select: (state) => state.location.pathname });
	const onboardingLock = isOnboardingPath(pathname);
	const isAdminPath = pathname.startsWith("/admin");
	const sendToOnboarding = workspaceNeedsOnboarding(user) && !onboardingLock && !isAdminPath;
	const licenseHoldsRoute =
		currentUserLacksLicense(rawUser) &&
		!licenseGateAllowsPath(pathname, {
			isAdmin: !!user && isAdmin(user),
			isManager: !!user && hasUserRoleManager(user),
		});
	useTimesRealtime();
	useProfileLanguage();
	useUserTheme();

	if (authMode === "guest") {
		return <Navigate to="/teams/login" />;
	}

	if (isInitialLoading) {
		return <SpinnerPage />;
	}

	return (
		<RouteGuard>
			<NavPermissionsProvider>
				{sendToOnboarding ? (
					<Navigate search={{ step: "name" }} to="/onboarding" />
				) : onboardingLock ? (
					<OnboardingDraftProvider>
						<div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
							<OnboardingChrome />
							<div className="min-h-0 flex-1">
								<Outlet />
							</div>
							<AppSessionOverlays />
						</div>
					</OnboardingDraftProvider>
				) : (
					<SidebarProvider
						className="h-full max-h-full min-h-0 overflow-hidden"
						onOpenChange={setStoredSidebarOpen}
						open={isSidenavOpen}
						style={
							{
								"--sidebar-width": "15rem",
								"--sidebar-width-icon": "3rem",
							} as CSSProperties
						}>
						<AppSidebar />
						<SidebarInset className="relative h-full min-h-0 min-w-0 overflow-visible bg-transparent md:peer-data-[variant=inset]:m-0 md:peer-data-[variant=inset]:rounded-none md:peer-data-[variant=inset]:shadow-none">
							<AppHeader />
							<div className="flex min-h-0 flex-1 flex-col overflow-hidden p-2 pt-0">
								<div
									className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-boxed-background shadow-sm"
									data-slot="boxed-content">
									{licenseHoldsRoute ? null : (
										<NavRouteGate>
											<Outlet />
										</NavRouteGate>
									)}
								</div>
							</div>
							<WorkspaceBillingGate />
						</SidebarInset>
						{usageBlocked ? null : (
							<>
								<LazyTimerWidgetHost />
								<LazyCreateTimeEntryDialogHost />
								<LazyCreateWorkingHoursDialogHost />
								<LazyWorkingHoursSuggestionDialogHost />
							</>
						)}
						<AppSessionOverlays />
					</SidebarProvider>
				)}
			</NavPermissionsProvider>
		</RouteGuard>
	);
};

export const Route = createFileRoute("/_app")({
	beforeLoad: async ({ location }) => {
		let permissionUser = toPermissionUser(stores.user.useStore.getState().value);
		if (!permissionUser) {
			try {
				permissionUser = toPermissionUser(await stores.user.actions.get());
			} catch {
				// RouteGuard handles unauthenticated sessions once the app layout mounts.
				return;
			}
		}
		if (!permissionUser) {
			return;
		}
		const nav = resolveNavPermissions(permissionUser, {
			email: emailFromRawUser(stores.user.useStore.getState().value),
		});
		if (!canAccessNavPath(location.pathname, nav)) {
			throw redirect({ replace: true, to: firstAllowedNavPath(nav) });
		}
	},
	component: AppLayout,
});
